import { ChangeEvent, FunctionComponent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faCircle } from '@fortawesome/pro-light-svg-icons';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import NumberField from 'components/UI/V3/Forms/NumberField';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import QuantityStepper from '../LotsSidebar/QuantityStepper';
import clsx from 'clsx';
import styles from './StockSidebar.module.scss';

export type StockAdjustmentMode = 'add' | 'reduce';

interface AdjustStockProps {
    mode: StockAdjustmentMode;
    quantity: number;
    unitCost: number | undefined;
    reason: string;
    currentStock: number;
    disabled?: boolean;
    onModeChange: (mode: StockAdjustmentMode) => void;
    onQuantityChange: (quantity: number) => void;
    onUnitCostChange: (unitCost: number | undefined) => void;
    onReasonChange: (reason: string) => void;
}

export const AdjustStock: FunctionComponent<AdjustStockProps> = ({
    mode,
    quantity,
    unitCost,
    reason,
    currentStock,
    disabled,
    onModeChange,
    onQuantityChange,
    onUnitCostChange,
    onReasonChange
}) => {
    const newStock = mode === 'add' ? currentStock + quantity : Math.max(0, currentStock - quantity);

    return (
        <div>
            <h3 className={styles.sectionTitle}>Adjust Stock</h3>
            <div className={styles.modeRow}>
                <button
                    type="button"
                    className={clsx(styles.modeButton, mode === 'add' && styles.modeButtonActive)}
                    onClick={() => onModeChange('add')}
                    disabled={disabled}
                >
                    <FontAwesomeIcon
                        icon={faPlus}
                        className={styles.modeIcon}
                    />
                    Add Stock
                    <FontAwesomeIcon
                        icon={mode === 'add' ? faCircleCheck : faCircle}
                        className={mode === 'add' ? styles.checkIcon : styles.radioIcon}
                    />
                </button>
                <button
                    type="button"
                    className={clsx(styles.modeButton, mode === 'reduce' && styles.modeButtonActive)}
                    onClick={() => onModeChange('reduce')}
                    disabled={disabled}
                >
                    <FontAwesomeIcon
                        icon={faMinus}
                        className={styles.modeIcon}
                    />
                    Reduce Stock
                    <FontAwesomeIcon
                        icon={mode === 'reduce' ? faCircleCheck : faCircle}
                        className={mode === 'reduce' ? styles.checkIcon : styles.radioIcon}
                    />
                </button>
            </div>
            <div className={styles.quantityRow}>
                <span className={styles.quantityLabel}>Quantity</span>
                <QuantityStepper
                    quantity={quantity}
                    disabled={disabled}
                    onChange={onQuantityChange}
                />
                <span className={styles.newStockChip}>New Stock: {newStock}</span>
            </div>
            <div className={clsx(styles.field, styles.unitCostField)}>
                <NumberField
                    id="stock-unit-cost"
                    name="unitCost"
                    label="Unit Cost"
                    value={unitCost}
                    onChange={onUnitCostChange}
                    scale={2}
                    precision={6}
                    allowEmptyValue
                    disabled={disabled}
                    startIcon={<span>$</span>}
                    className={styles.unitCostInput}
                    placeholder="0.00"
                />
            </div>
            <div className={clsx(styles.field, styles.reasonField)}>
                <Textarea
                    id="stock-reason"
                    label="Reason"
                    placeholder="Enter the reason"
                    required
                    value={reason}
                    maxLength={100}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onReasonChange(event.target.value)}
                    disabled={disabled}
                    rows={1}
                />
            </div>
        </div>
    );
};

export default AdjustStock;
